import React, { useRef, useState } from 'react';
import { getStoredToken } from '../services/authSession';
import { CheckCircle, AlertTriangle, HelpCircle, Upload, Download } from 'lucide-react';
import { useClient } from '../context/ClientContext';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

/**
 * Client Closeout — the end-of-engagement review.
 *
 * The workflow is deliberately upload-driven: export the A-K schedules, review
 * and adjust them, then upload that workbook back here so the adequacy checks
 * run against *the file you actually have* (manual edits included) rather than
 * the server's copy of whatever was last generated.
 */
export default function ClientCloseoutPage() {
  const { clientName, hasActiveClient } = useClient();
  const [adequacy, setAdequacy] = useState(null);
  const [running, setRunning] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const fileRef = useRef(null);

  const authHeaders = () => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const downloadFromApi = async (url, filename) => {
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(a.href);
  };

  const runOnUpload = async (fileObj) => {
    if (!fileObj) return;
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      const res = await fetch(`${API}/gl-calcs/adequacy/upload`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      const report = await res.json();
      setAdequacy(report);
      setUploadId(report.upload_id || null);
    } catch (e) {
      window.alert(`Adequacy review failed: ${e.message || e}`);
    } finally {
      setRunning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadReviewed = async () => {
    if (!uploadId) return;
    try {
      await downloadFromApi(
        `${API}/gl-calcs/adequacy/upload/${encodeURIComponent(uploadId)}/reviewed`,
        'adequacy_REVIEWED.xlsx',
      );
    } catch (e) {
      window.alert(`Download failed: ${e.message || e}`);
    }
  };

  const s = adequacy?.summary || {};
  const failed = (s.fail || 0) > 0;
  const unresolved = (s.unresolved || 0) > 0;
  const fmt = (v) => (typeof v === 'number'
    ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : (v ?? ''));

  // Headline must never overstate: "all pass" only when nothing failed AND
  // nothing was left unchecked.
  let tone = { bg: '#f0fdf4', border: '#86efac', fg: '#166534', Icon: CheckCircle, iconColor: '#16a34a' };
  let headline = 'All tie-outs pass';
  if (failed) {
    tone = { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b', Icon: AlertTriangle, iconColor: '#dc2626' };
    headline = `${s.fail} tie-out${s.fail === 1 ? '' : 's'} do not tie`;
  } else if (unresolved) {
    tone = { bg: '#fffbeb', border: '#fcd34d', fg: '#92400e', Icon: HelpCircle, iconColor: '#d97706' };
    headline = `Checked tie-outs pass, ${s.unresolved} not checked`;
  }

  const rows = (adequacy?.results || []).filter(r => r.status !== 'pass');

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">Client Closeout</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <span className="reconcile-meta-label">Client:</span>
            <span>{hasActiveClient() ? clientName : 'No client selected'}</span>
          </div>
        </div>
      </div>

      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <h3 className="pro-card-title">Adequacy Review</h3>
        </div>
        <div style={{ padding: '1.25rem' }}>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.925rem' }}>
            Export the Schedules A-K, review and adjust them as needed, then upload that
            workbook here. The review checks it against itself: every pool's rate equals
            pool ÷ base, the summary agrees with the master schedule, and each detail
            schedule foots to its pool. Anything that does not tie is listed below and
            highlighted in red in the reviewed copy.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            style={{ display: 'none' }}
            onChange={(e) => runOnUpload(e.target.files && e.target.files[0])}
          />
          <button
            className="pro-btn pro-btn-primary"
            style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: 600, opacity: running ? 0.7 : 1 }}
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={running}
          >
            <Upload style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
            {running ? 'Reviewing…' : 'Upload Export & Run Adequacy Review'}
          </button>
        </div>
      </div>

      {adequacy && (
        <div className="pro-card" style={{ border: `1px solid ${tone.border}` }}>
          <div style={{ padding: '1rem 1.25rem', background: tone.bg, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <tone.Icon style={{ width: '1.5rem', height: '1.5rem', color: tone.iconColor, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: tone.fg }}>Adequacy Review: {headline}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                {s.pass} pass · {s.fail} fail · {s.unresolved} unresolved of {s.total} checks
                across {adequacy.sections} sections
                {adequacy.filename ? ` · reviewed ${adequacy.filename}` : ''}
              </div>
            </div>
            <button className="pro-btn pro-btn-secondary" style={{ padding: '0.5rem 1rem', fontWeight: 600 }}
              onClick={downloadReviewed}>
              <Download style={{ width: '1rem', height: '1rem', marginRight: '0.4rem' }} />
              Download reviewed export
            </button>
          </div>

          {rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', background: 'var(--gray-50)' }}>
                    <th style={{ padding: '0.5rem 1rem' }}>Status</th>
                    <th style={{ padding: '0.5rem 1rem' }}>Check</th>
                    <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Left</th>
                    <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Right</th>
                    <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '0.5rem 1rem', fontWeight: 600, color: r.status === 'fail' ? '#dc2626' : '#d97706' }}>
                        {r.status === 'fail' ? 'FAIL' : 'NOT CHECKED'}
                      </td>
                      <td style={{ padding: '0.5rem 1rem' }}>
                        {r.description}
                        {r.note && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '0.15rem' }}>{r.note}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{fmt(r.left_value)}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{fmt(r.right_value)}</td>
                      <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{fmt(r.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
